/**
 * RoleGate — compara role desejada (URL) com role autoritativa (backend).
 *
 * Casos:
 *   - URL=master + backend=master  → renderiza children (GameMasterPanel)
 *   - URL=player + backend=player  → renderiza children (GamePlayerView)
 *   - URL=master + backend=player  → mostra tela de erro (precisa logar
 *                                    como mestre da sessão)
 *   - backend=master + URL=player  → renderiza children como player
 *                                    (master logou mas quer ver como aluno — OK)
 *
 * Exibe skeleton enquanto snapshot não chegou.
 */

import type { ReactNode } from 'react'

import { useSession } from '../session'
import { useSessionState } from '../hooks/useSessionState'

interface Props {
  children: ReactNode
}

export function RoleGate({ children }: Props) {
  const session = useSession()
  const { actualRole, snapshotReceived } = useSessionState()

  if (!snapshotReceived) {
    return (
      <div
        data-module-lab-root
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-lab-bg, #FAFAF7)',
          color: 'var(--color-lab-ink-3, #555B66)',
          fontFamily: 'var(--font-lab-sans, system-ui, sans-serif)',
        }}
      >
        <div>Conectando ao servidor…</div>
      </div>
    )
  }

  // URL pede master mas backend resolveu player → bloqueio.
  if (session.role === 'master' && actualRole !== 'master') {
    return <MasterAuthRequired sessionId={session.sessionId} />
  }

  return <>{children}</>
}

function MasterAuthRequired({ sessionId }: { sessionId: string }) {
  const loginUrl = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`

  return (
    <div
      data-module-lab-root
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--spacing-lab-4)',
        background: 'var(--color-lab-bg, #FAFAF7)',
        color: 'var(--color-lab-ink, #0F1115)',
        fontFamily: 'var(--font-lab-sans, system-ui, sans-serif)',
      }}
    >
      <div
        style={{
          maxWidth: 520,
          padding: 'var(--spacing-lab-5) var(--spacing-lab-4)',
          background: 'var(--color-lab-bg-2, #F2F2ED)',
          border: '1px solid var(--color-lab-rule, #D8D5CB)',
          borderRadius: 'var(--radius-lab, 12px)',
          boxShadow: 'var(--lab-shadow)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs)',
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'var(--color-lab-rose, #D4474A)',
            marginBottom: 8,
            fontWeight: 700,
          }}
        >
          Acesso de mestre negado
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--text-lab-xl)',
            lineHeight: 1.25,
          }}
        >
          Você precisa estar logado como mestre desta sessão.
        </h1>
        <p
          style={{
            marginTop: 'var(--spacing-lab-3)',
            marginBottom: 'var(--spacing-lab-4)',
            color: 'var(--color-lab-ink-3)',
            lineHeight: 1.55,
          }}
        >
          A URL pede papel de mestre (<code>?role=master</code>), mas o
          servidor reconheceu esta conexão como aluno anônimo — seu JWT
          está ausente ou não corresponde ao criador desta sessão.
        </p>
        <p
          style={{
            fontFamily: 'var(--font-lab-mono)',
            fontSize: 'var(--text-lab-xs)',
            color: 'var(--color-lab-ink-3)',
            marginBottom: 'var(--spacing-lab-4)',
          }}
        >
          Session: <code>{sessionId}</code>
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a
            href={loginUrl}
            style={{
              minHeight: 44,
              padding: '10px 18px',
              background: 'var(--color-lab-accent, #5B2DB8)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 'var(--radius-lab-sm, 6px)',
              fontFamily: 'var(--font-lab-mono)',
              fontSize: 'var(--text-lab-sm)',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Ir para o login
          </a>
          <a
            href={`${window.location.pathname}?role=player`}
            style={{
              minHeight: 44,
              padding: '10px 18px',
              background: 'transparent',
              color: 'var(--color-lab-ink)',
              textDecoration: 'none',
              borderRadius: 'var(--radius-lab-sm, 6px)',
              border: '1px solid var(--color-lab-rule)',
              fontFamily: 'var(--font-lab-mono)',
              fontSize: 'var(--text-lab-sm)',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Entrar como aluno
          </a>
        </div>
      </div>
    </div>
  )
}
